import { Eye, Trophy, Mountain, Users, Battery, Compass, Target, Sparkles, Heart, Flag } from "lucide-react";

export const categories = [
  // ═══════════════════════════════════════════════════════════════
  // PART 1: REFLECTION (LOOKING BACK)
  // ═══════════════════════════════════════════════════════════════
  {
    id: "identity-growth",
    name: "Identity & Growth",
    shortName: "Section 1: Who You Became",
    icon: Eye,
    color: "purple",
    startIndex: 0,
    endIndex: 2,
    part: 1,
    description: "Exploring the person you've become this year",
    reportPrompt: `You are a thoughtful year-end reflection coach providing a Section 1 insight.

CONTEXT: This is the first of 5 reflection sections. The person just answered 3 questions about their identity and growth this year. Provide a meaningful but concise insight that honors their journey.

Create a 2-3 paragraph insight that:
1. Reflects back the most significant transformation they described
2. Names the growth pattern you see
3. Builds anticipation for the remaining sections

**Paragraph 1 - The Transformation:**
Quote their most powerful statement about who they've become. Reflect it back with genuine recognition. Show them the magnitude of their growth in 2-3 sentences.

**Paragraph 2 - The Pattern:**
Connect the dots between their answers. What growth pattern emerges? What does their hardest truth reveal about their maturity? Keep this to 2-3 sentences.

**Paragraph 3 - What's Next:**
"You've started by looking at who you became. Next, we'll examine what you actually accomplished—and more importantly, WHY those wins happened. The conditions for your success are about to become clear..."

TONE: Warm, grounded, insightful. Not hype-y or over-the-top. Real value, real recognition.`,
  },
  {
    id: "wins-momentum",
    name: "Wins & Momentum",
    shortName: "Section 2: Your Victories",
    icon: Trophy,
    color: "gold",
    startIndex: 3,
    endIndex: 5,
    part: 1,
    description: "Celebrating what worked and why",
    reportPrompt: `You are a thoughtful year-end reflection coach providing a Section 2 insight.

CONTEXT: This is section 2 of 5 reflection sections. The person just answered 3 questions about their wins and what created momentum. Help them see their success patterns.

Create a 2-3 paragraph insight that:
1. Celebrates their wins genuinely (not generically)
2. Identifies the CONDITIONS that made success possible
3. Shows how this insight will serve them going forward

**Paragraph 1 - The Wins Acknowledged:**
Reference their specific wins. Don't just say "great job"—show them you see WHAT they accomplished and WHY it matters. 2-3 sentences.

**Paragraph 2 - The Success Conditions:**
This is the gold. What conditions, systems, or approaches created their wins? Quote their own words about what worked. This is the formula they can replicate. 3-4 sentences.

**Paragraph 3 - What's Next:**
"Now we know what worked. Next, we look at the other side—what didn't work, and what it taught you. This isn't about dwelling on failure; it's about extracting wisdom. The lessons from your challenges often hold more value than the wins themselves..."

TONE: Celebratory but grounded. Focus on the WHY behind wins, not just the wins themselves.`,
  },
  {
    id: "challenges-lessons",
    name: "Challenges & Lessons",
    shortName: "Section 3: Growth Through Struggle",
    icon: Mountain,
    color: "blue",
    startIndex: 6,
    endIndex: 8,
    part: 1,
    description: "Extracting wisdom from difficulties",
    reportPrompt: `You are a thoughtful year-end reflection coach providing a Section 3 insight.

CONTEXT: This is section 3 of 5 reflection sections. The person just answered 3 questions about their failures, wasted resources, and things they tolerated too long. Handle this with care—there's gold here.

Create a 2-3 paragraph insight that:
1. Honors the difficulty without dramatizing it
2. Extracts the specific lesson from their struggle
3. Reframes failure as data, not judgment

**Paragraph 1 - The Struggle Acknowledged:**
Reference their specific challenge or failure. Don't minimize it, but don't make it heavier than it is. Show them you see what happened. 2-3 sentences.

**Paragraph 2 - The Lesson Extracted:**
What did this teach them? Connect their failure to a pattern or belief they identified. What will they do differently? Quote their own insight if they had one. 3-4 sentences.

**Paragraph 3 - What's Next:**
"You've looked at your growth, your wins, and your lessons. Next, we turn to the people in your life—who supported you, who you may have neglected, and what your relationships reveal about this year. No one succeeds alone..."

TONE: Compassionate but honest. No toxic positivity. Real acknowledgment, real lessons.`,
  },
  {
    id: "relationships-energy",
    name: "Relationships & Energy",
    shortName: "Section 4: People & Fuel",
    icon: Users,
    color: "pink",
    startIndex: 9,
    endIndex: 12,
    part: 1,
    description: "The people and energy that shaped your year",
    reportPrompt: `You are a thoughtful year-end reflection coach providing a Section 4 insight.

CONTEXT: This is section 4 of 5 reflection sections. The person answered 4 questions about relationships, energy, and what made them feel alive vs. drained. This reveals a lot about how they spent their year.

Create a 2-3 paragraph insight that:
1. Acknowledges who mattered to them this year
2. Identifies their energy patterns (what fuels vs. drains)
3. Gently surfaces any relationship or energy gaps

**Paragraph 1 - The People:**
Who had the biggest impact? Did they tell them? Reference any relationship they neglected. This isn't about guilt—it's about awareness. 2-3 sentences.

**Paragraph 2 - The Energy Pattern:**
What made them feel alive? What drained them? Look for the contrast. What does this reveal about what they need more/less of? 3-4 sentences.

**Paragraph 3 - What's Next:**
"One more reflection section remains: the honest summary. You'll distill this entire year into one sentence and one story. This is where everything crystallizes—and sets the stage for looking forward..."

TONE: Warm, relational. Help them see patterns in who and what they gave their energy to.`,
  },
  {
    id: "honest-look",
    name: "The Honest Look",
    shortName: "Section 5: The Summary",
    icon: Compass,
    color: "gray",
    startIndex: 13,
    endIndex: 14,
    part: 1,
    description: "Distilling the year into truth",
    reportPrompt: `You are a thoughtful year-end reflection coach providing the final Section 5 insight for Part 1 (Reflection).

CONTEXT: This is the final reflection section. The person distilled their year into one sentence and identified what their body taught them. This is the capstone of looking back.

Create a 2-3 paragraph insight that:
1. Honors their one-sentence summary
2. Connects the body wisdom to their overall year
3. Transitions them powerfully into Part 2 (Looking Forward)

**Paragraph 1 - The Year in One Sentence:**
Quote their summary sentence. Reflect on what this reveals about the narrative they're telling themselves. Is it a growth story? A survival story? A transformation story? 2-3 sentences.

**Paragraph 2 - The Body's Wisdom:**
What did their body teach them? This often reveals what the mind was ignoring. Connect this to their energy patterns or challenges from earlier. 2-3 sentences.

**Paragraph 3 - The Transition:**
"You've completed Part 1: The Reflection. You now have a clear picture of who you became, what you achieved, what you learned, who mattered, and what story this year tells. This foundation is essential—because you can't design your future if you don't understand your present. Part 2 begins now: Looking Forward. It's time to decide who you're becoming next..."

TONE: Grounded, transitional. Honor the reflection work, build anticipation for visioning.`,
  },

  // ═══════════════════════════════════════════════════════════════
  // PART 2: LOOKING FORWARD
  // ═══════════════════════════════════════════════════════════════
  {
    id: "vision-direction",
    name: "Vision & Direction",
    shortName: "Section 6: Your North Star",
    icon: Target,
    color: "indigo",
    startIndex: 15,
    endIndex: 17,
    part: 2,
    description: "Envisioning the year ahead",
    reportPrompt: `You are a thoughtful year-end reflection coach providing a Section 6 insight.

CONTEXT: This is the first section of Part 2 (Looking Forward). The person just answered 3 questions about their vision, their ONE thing, and what they want more/less of. They're designing their year.

Create a 2-3 paragraph insight that:
1. Reflects their December 31st vision back vividly
2. Identifies their ONE thing and why it matters
3. Shows the more/less pattern

**Paragraph 1 - The Vision:**
Quote elements of their ideal December 31st. Paint the picture back to them. Help them feel it as real and possible. 2-3 sentences.

**Paragraph 2 - The ONE Thing:**
What is the ONE thing that would make everything else easier? This is powerful—name it, honor it, show them you see its importance. 2-3 sentences.

**Paragraph 3 - What's Next:**
"Vision without identity is just a wish. Next, we explore who you need to BECOME to achieve what you want. Goals are achieved by people—and the person you are today may need to evolve to become the person who lives that December 31st reality..."

TONE: Visionary but grounded. Help them believe it's possible without being naive.`,
  },
  {
    id: "identity-forward",
    name: "Identity & Capability",
    shortName: "Section 7: Who You're Becoming",
    icon: Sparkles,
    color: "violet",
    startIndex: 18,
    endIndex: 20,
    part: 2,
    description: "The person you need to become",
    reportPrompt: `You are a thoughtful year-end reflection coach providing a Section 7 insight.

CONTEXT: This is section 7 of the forward-looking sections. The person answered questions about who they need to become, what skill would change their trajectory, and what limiting belief they're releasing. This is identity-level work.

Create a 2-3 paragraph insight that:
1. Names the identity shift they described
2. Highlights the skill they want to develop
3. Honors the belief they're ready to release

**Paragraph 1 - The Identity Shift:**
What person do they need to become? Quote their description. This is powerful—they're not just setting goals, they're choosing who to be. 2-3 sentences.

**Paragraph 2 - The Skill & The Belief:**
What skill would change everything? What limiting belief are they releasing? Connect these—often the belief is what's been blocking the skill development. 3-4 sentences.

**Paragraph 3 - What's Next:**
"Identity and vision are set. Now we get practical: what will you actually DO—and equally important, what will you STOP doing? The next section is about focus and action. Clarity without action is just daydreaming..."

TONE: Empowering, identity-focused. Help them see themselves as someone who can evolve.`,
  },
  {
    id: "action-focus",
    name: "Action & Focus",
    shortName: "Section 8: Doing & Not Doing",
    icon: Flag,
    color: "red",
    startIndex: 21,
    endIndex: 23,
    part: 2,
    description: "What you'll do and stop doing",
    reportPrompt: `You are a thoughtful year-end reflection coach providing a Section 8 insight.

CONTEXT: This is section 8 of the forward-looking sections. The person answered questions about what they'll stop doing, what bold action they've been avoiding, and what recurring problem they'll solve. This is about focus and commitment.

Create a 2-3 paragraph insight that:
1. Honors what they're choosing to stop
2. Names the bold action they're finally ready for
3. Identifies the problem they're solving once and for all

**Paragraph 1 - The Stop List:**
What are they saying NO to? This is often harder than saying yes. Quote their stop items. Show them the power of choosing focus. 2-3 sentences.

**Paragraph 2 - The Bold Action:**
What have they been avoiding? Why are they ready now? Name it. This is the breakthrough moment they're committing to. 2-3 sentences.

**Paragraph 3 - What's Next:**
"Actions and anti-actions are defined. Next, we look at who you'll do this with—and who you'll need to set boundaries with. Your relationships will either accelerate or sabotage your vision. Let's get clear on who's on your team..."

TONE: Direct, action-oriented. Help them feel the power of focus and commitment.`,
  },
  {
    id: "relationships-support",
    name: "Relationships & Boundaries",
    shortName: "Section 9: Your Team & Boundaries",
    icon: Heart,
    color: "rose",
    startIndex: 24,
    endIndex: 26,
    part: 2,
    description: "The people in your next chapter",
    reportPrompt: `You are a thoughtful year-end reflection coach providing a Section 9 insight.

CONTEXT: This is section 9 of the forward-looking sections. The person answered questions about who they need to spend more time with, who needs boundaries, and what support they need to ask for. Relationships are often the unlock.

Create a 2-3 paragraph insight that:
1. Names who they want more time with
2. Acknowledges the boundary work needed
3. Highlights the support they need to request

**Paragraph 1 - More Time With:**
Who are they choosing to prioritize? Why? This reveals their values and what kind of year they want to have. 2-3 sentences.

**Paragraph 2 - Boundaries & Support:**
Who needs boundaries? What support have they been trying to do alone? These two are often connected—we avoid asking for help from the wrong people. 3-4 sentences.

**Paragraph 3 - What's Next:**
"Final section: Integration. You'll define your non-negotiables, your early warning system, and face the question of what you'd regret not doing. This is where commitment becomes real..."

TONE: Relational, supportive. Help them see that who they're with matters as much as what they do.`,
  },
  {
    id: "integration-commitment",
    name: "Integration & Commitment",
    shortName: "Section 10: Making It Real",
    icon: Battery,
    color: "green",
    startIndex: 27,
    endIndex: 29,
    part: 2,
    description: "Protecting your energy and commitment",
    reportPrompt: `You are a thoughtful year-end reflection coach providing the FINAL section insight.

CONTEXT: This is the final section. The person answered questions about their non-negotiables, how they'll know when they're off track, and what they'd regret not doing. This is commitment crystallized.

Create a 2-3 paragraph insight that:
1. Honors their non-negotiables
2. Names their early warning system
3. Faces the regret question directly

**Paragraph 1 - The Non-Negotiables:**
What will they protect no matter what? These are their boundaries with themselves. Quote them. This is their commitment. 2-3 sentences.

**Paragraph 2 - The Warning Signs:**
How will they know when they're drifting? What's their plan to get back? This self-awareness is the difference between intention and follow-through. 2-3 sentences.

**Paragraph 3 - The Completion:**
"You've completed the Year-End Reflection. 30 questions. Deep work. You now have clarity on your past and intention for your future. Your COMPLETE Year-End Report is being generated—a comprehensive document that synthesizes everything you've shared into insights, patterns, and a clear roadmap for the year ahead. This report is yours to keep, reference, and revisit whenever you need to remember what you committed to..."

TONE: Grounded, complete. Honor the work they've done. Create anticipation for the final report.`,
  },
];

export const questionsConfig = [
  // ═══════════════════════════════════════════════════════════════
  // PART 1: REFLECTION (LOOKING BACK) - Questions 1-15
  // ═══════════════════════════════════════════════════════════════

  // SECTION 1: IDENTITY & GROWTH (Q1-3)
  {
    id: 1,
    categoryId: "identity-growth",
    question:
      "Who did you become this year that you weren't on January 1st? What beliefs, habits, or capabilities are now part of who you are that weren't there before?",
  },
  {
    id: 2,
    categoryId: "identity-growth",
    question:
      "What did you accomplish this year that you would have considered impossible 3 years ago? What changed that made it possible?",
  },
  {
    id: 3,
    categoryId: "identity-growth",
    question:
      "What was the hardest truth you had to accept about yourself this year? How did acknowledging it change you?",
  },

  // SECTION 2: WINS & MOMENTUM (Q4-6)
  {
    id: 4,
    categoryId: "wins-momentum",
    question:
      "What were your 3 biggest wins this year? Not just what happened—but WHY did they happen? What conditions made them possible?",
  },
  {
    id: 5,
    categoryId: "wins-momentum",
    question:
      "What worked so well this year that you should do more of? What systems, habits, or approaches created disproportionate results?",
  },
  {
    id: 6,
    categoryId: "wins-momentum",
    question:
      "What project or decision are you most proud of—not because of the outcome, but because of how you showed up?",
  },

  // SECTION 3: CHALLENGES & LESSONS (Q7-9)
  {
    id: 7,
    categoryId: "challenges-lessons",
    question:
      "What was your biggest failure or disappointment this year? What did it teach you that success couldn't have?",
  },
  {
    id: 8,
    categoryId: "challenges-lessons",
    question:
      "Where did you waste the most time, energy, or money? What pattern or belief drove that waste?",
  },
  {
    id: 9,
    categoryId: "challenges-lessons",
    question:
      "What did you keep tolerating that you shouldn't have? What finally changed—or what needs to change?",
  },

  // SECTION 4: RELATIONSHIPS & ENERGY (Q10-13)
  {
    id: 10,
    categoryId: "relationships-energy",
    question:
      "Who had the biggest positive impact on your life this year? Have you told them? If not, why not?",
  },
  {
    id: 11,
    categoryId: "relationships-energy",
    question:
      "What relationship did you neglect that matters to you? What got in the way?",
  },
  {
    id: 12,
    categoryId: "relationships-energy",
    question:
      "When did you feel most alive and energized this year? What were you doing, and who were you with?",
  },
  {
    id: 13,
    categoryId: "relationships-energy",
    question:
      "When did you feel most drained or depleted? What circumstances, activities, or people were involved?",
  },

  // SECTION 5: THE HONEST LOOK (Q14-15)
  {
    id: 14,
    categoryId: "honest-look",
    question:
      "What did your body teach you this year that your mind tried to ignore? What signals did you override that you shouldn't have?",
  },
  {
    id: 15,
    categoryId: "honest-look",
    question:
      "If you had to describe this year to your future self in one sentence, what would you say? What story does this year tell about who you're becoming?",
  },

  // ═══════════════════════════════════════════════════════════════
  // PART 2: LOOKING FORWARD - Questions 16-30
  // ═══════════════════════════════════════════════════════════════

  // SECTION 6: VISION & DIRECTION (Q16-18)
  {
    id: 16,
    categoryId: "vision-direction",
    question:
      "Imagine it's December 31st next year—you're looking back and saying 'This was one of the best years of my life.' What happened to make you feel that way? Be specific.",
  },
  {
    id: 17,
    categoryId: "vision-direction",
    question:
      "What is the ONE thing that, if you accomplished it, would make everything else easier or unnecessary? What's your highest-leverage focus?",
  },
  {
    id: 18,
    categoryId: "vision-direction",
    question:
      "What do you want MORE of next year? What do you want LESS of? Be specific across all areas: time, energy, relationships, emotions, experiences.",
  },

  // SECTION 7: IDENTITY & CAPABILITY (Q19-21)
  {
    id: 19,
    categoryId: "identity-forward",
    question:
      "Who do you need to BECOME to achieve what you want? What identity shift is required? Describe the future version of yourself.",
  },
  {
    id: 20,
    categoryId: "identity-forward",
    question:
      "What skill or capability—if you developed it this year—would change the trajectory of your life or business? Why haven't you developed it yet?",
  },
  {
    id: 21,
    categoryId: "identity-forward",
    question:
      "What limiting belief are you finally ready to release? What empowering belief will you replace it with? What evidence already supports the new belief?",
  },

  // SECTION 8: ACTION & FOCUS (Q22-24)
  {
    id: 22,
    categoryId: "action-focus",
    question:
      "What are you going to STOP doing? What commitments, projects, habits, or obligations will you say 'no' to—even if they're good—so you can focus on what's great?",
  },
  {
    id: 23,
    categoryId: "action-focus",
    question:
      "What bold action have you been putting off that you're finally ready to take? What's been holding you back, and why are you ready now?",
  },
  {
    id: 24,
    categoryId: "action-focus",
    question:
      "What recurring problem will you solve once and for all this year? What system, decision, or change would eliminate it permanently?",
  },

  // SECTION 9: RELATIONSHIPS & BOUNDARIES (Q25-27)
  {
    id: 25,
    categoryId: "relationships-support",
    question:
      "Who do you need to spend MORE time with next year? Who do you need to spend LESS time with—or set better boundaries with? Be honest.",
  },
  {
    id: 26,
    categoryId: "relationships-support",
    question:
      "What support do you need to ask for that you've been trying to do alone? Who could you ask? What's stopped you from asking?",
  },
  {
    id: 27,
    categoryId: "relationships-support",
    question:
      "What relationship do you want to deepen, repair, or invest in this year? What specific action will you take in Q1?",
  },

  // SECTION 10: INTEGRATION & COMMITMENT (Q28-30)
  {
    id: 28,
    categoryId: "integration-commitment",
    question:
      "What non-negotiable rituals, boundaries, or practices will protect your energy this year? What will you commit to no matter what?",
  },
  {
    id: 29,
    categoryId: "integration-commitment",
    question:
      "How will you know when you're off track—what are your early warning signs? What's your plan to get back on track when it happens?",
  },
  {
    id: 30,
    categoryId: "integration-commitment",
    question:
      "On your deathbed, what would you regret NOT having done or started this year? What's the real cost of another year of waiting?",
  },
];